import { WidgetComponent } from '@juvare/widget-ui/WidgetComponent';
import { WidgetEditorContext } from '@juvare/widget-ui/WidgetEditorContext';
import { Box } from '@mui/material';
import React from 'react';
import { DragTargetPlaceholder } from '../../WidgetForm/Components';
import { getChildWidgetStyle, getVisibleError } from '../../WidgetForm/Utils';
import { DropTargetSpacerHorizontal } from './DropTargetSpacerHorizontal';
/**
 * A layout column component for rendering and aligning child widgets inside a layout editor.
 * Filters out hidden widgets when not in edit mode and renders drop targets for drag-and-drop editing.
 *
 * @param {Props} props - Layout column configuration, including spacing, alignment, child widgets, and edit mode settings.
 * @param {number} props.spacing - Vertical spacing (in pixels) between child widgets.
 * @param {'left' | 'right' | 'center'} [props.horizontalAlign] - Optional horizontal alignment of child widgets inside the column.
 * @param {string} props.childrenEditorPath - Editor path string used for identifying the column in the widget editor.
 * @param {WidgetProps<any, any>[]} props.childProps - Array of widget properties describing each child component to render in the column.
 * @param {WidgetRenderData} props.renderData - Data object providing render-time context for the widgets.
 * @param {boolean} [props.editMode] - Whether the layout is currently in edit mode (shows drop targets and hidden widgets).
 * @param {string} [props.dragTargetPlaceholderMessage] - Optional custom placeholder message shown when the column is empty in edit mode.
 * @param {SxProps<Theme>} [props.sx] - Optional MUI `sx` style overrides for the root container.
 * @returns {JSX.Element} The rendered layout column with child widgets and drop targets.
 */
export const BaseLayoutColumn = (props) => {
    var _a;
    const editorContext = React.useContext(WidgetEditorContext);
    let childProps = props.childProps;
    if (!editorContext.editMode) {
        childProps = childProps.filter((p) => !p.hidden);
    }
    let commonChildStyle = undefined;
    switch (props.horizontalAlign) {
        case 'left':
            commonChildStyle = { marginRight: 'auto' };
            break;
        case 'right':
            commonChildStyle = { marginLeft: 'auto' };
            break;
        case 'center':
            commonChildStyle = { marginLeft: 'auto', marginRight: 'auto' };
            break;
    }
    return (React.createElement(Box, { sx: Object.assign(Object.assign({}, props.sx), { display: 'block' }), "data-editor-path": props.childrenEditorPath },
        childProps.length === 0 && (React.createElement(DropTargetSpacerHorizontal, { spacing: props.spacing, first: true, last: true, editorPath: '#0+', placeholderElement: props.editMode ? (React.createElement(DragTargetPlaceholder, null, (_a = props.dragTargetPlaceholderMessage) !== null && _a !== void 0 ? _a : 'Drag and drop components from the toolbar here')) : undefined })),
        childProps.length > 0 &&
            childProps.map((childProp, index) => (React.createElement(React.Fragment, { key: childProp.id },
                React.createElement(DropTargetSpacerHorizontal, { spacing: props.spacing, first: index === 0, last: false, editorPath: '#' + index + '+' }),
                React.createElement(Box, { style: getChildWidgetStyle(childProp, commonChildStyle), "data-editor-path": '#' + index, "data-widget": childProp.dataPath, "data-visible-error": getVisibleError(childProp) ? 'true' : undefined },
                    React.createElement(WidgetComponent, Object.assign({}, childProp, { renderData: props.renderData })))))),
        childProps.length > 0 && (React.createElement(DropTargetSpacerHorizontal, { spacing: props.spacing, first: false, last: true, editorPath: '#' + childProps.length + '+' }))));
};
